import PaymentStatusIcon from '@portal/components/PaymentStatusIcon/PaymentStatusIcon';
import StatementProofModal from '@portal/components/StatementProofModal/StatementProofModal';
import { StatementProofType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { formatMoney, maskCpfOrCnpj } from '@portal/services/masks';
import { Col, Row } from 'antd';
import { DateTime } from 'luxon';
import React, { useState } from 'react';

interface TransactionDetail {
  id: string;
  status: number;
  transactionAt: string;
  amount: number;
  description: string;
  origin: {
    paymentAccount: {
      bankAccount: string;
      bankBranchAccount: string;
    };
    account: {
      taxIdentifier: string;
      name: string;
    };
  };
  destiny: {
    paymentAccount: {
      bankAccount: string;
      bankBranchAccount: string;
    };
    account: {
      taxIdentifier: string;
      name: string;
    };
  };
}

interface TransactionSuccessDetailsProps {
  transaction: TransactionDetail;
}

const TransactionSuccessDetails = (props: TransactionSuccessDetailsProps) => {
  const { transaction } = props;
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);

  const handleCloseModal = () => {
    setIsShareModalVisible(false);
  };

  return (
    <div className="transaction-success-details">
      <div className="transaction-success-details__header">
        <PaymentStatusIcon status="completed" />
        <h2>{transaction.description}</h2>
        <p className="transaction-success-details__amount">
          {translate('SHARED.CURRENCY.SYMBOL')}
          {formatMoney(transaction.amount)}
        </p>
        <p className="transaction-success-details__date">
          {DateTime.fromISO(transaction.transactionAt).toLocaleString(
            DateTime.DATETIME_SHORT
          )}
        </p>
      </div>

      <div className="transaction-success-details__content">
        <Row gutter={24}>
          <Col span={12}>
            <div className="transaction-success-details__row">
              <span className="transaction-success-details__label">
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SOURCE_ACCOUNT'
                )}
              </span>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.BRANCH'
                )}
                {': '}
                {transaction.origin.paymentAccount.bankBranchAccount}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.ACCOUNT'
                )}
                {': '}
                {transaction.origin.paymentAccount.bankAccount}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.OWNER'
                )}
                {': '}
                {transaction.origin.account.name.toUpperCase()}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TAX_IDENTIFIER'
                )}
                {': '}
                {maskCpfOrCnpj(transaction.origin.account.taxIdentifier)}
              </p>
            </div>
          </Col>
          <Col span={12}>
            <div className="transaction-success-details__row">
              <span className="transaction-success-details__label">
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DESTINY_ACCOUNT'
                )}
              </span>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.BRANCH'
                )}
                {': '}
                {transaction.destiny.paymentAccount.bankBranchAccount}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.ACCOUNT'
                )}
                {': '}
                {transaction.destiny.paymentAccount.bankAccount}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.OWNER'
                )}
                {': '}
                {transaction.destiny.account.name.toUpperCase()}
              </p>
              <p>
                {translate(
                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TAX_IDENTIFIER'
                )}
                {': '}
                {maskCpfOrCnpj(transaction.destiny.account.taxIdentifier)}
              </p>
            </div>
          </Col>
        </Row>
      </div>
      <StatementProofModal
        visible={isShareModalVisible}
        onClose={handleCloseModal}
        hideEmail
        transaction={transaction}
        username={transaction.origin.account.name}
        title={translate('COMPONENTS.USERS_TRANSACTIONS.DETAILS.TITLE')}
        statementProofType={StatementProofType.TRANSACTION}
      />
    </div>
  );
};

export default TransactionSuccessDetails;
